/**
 * sticky-add-to-cart.js
 *
 * Scroll-trigger reveal + footer-hide behavior for the Sticky Add to Cart section.
 * Ported from assets/best-product.js `initStickyCta` (lines 143–222) and adapted
 * for use as a standalone section that can appear on any template.
 *
 * Behavior:
 * - Starts hidden (HTML `hidden` attribute). JS removes it and controls visibility
 *   via the `is-visible` class.
 * - Becomes visible once the user has scrolled past either:
 *     a) A custom trigger element identified by `data-trigger-id`, or
 *     b) A scroll-percentage threshold (`data-scroll-percentage` > 0).
 * - Re-hides when the page footer (`[role="contentinfo"]` or the Shopify footer
 *   section) enters the viewport.
 * - Re-hides while a "hide over" section is on screen (`data-hide-over-selector`,
 *   by default the Featured Product section), returning once it has scrolled by.
 *   Same standing-down rule as the footer: that section carries its own buy
 *   button, and two competing add-to-carts on screen at once is the thing this
 *   avoids.
 * - Supports multiple section instances on the same page.
 *
 * JS pattern: IIFE, no globals, re-inits on `shopify:section:load` for the
 * theme editor.
 */
(function () {
  'use strict';

  var SELECTORS = {
    stickyCta: '[data-sticky-cta]',
    footer: '[role="contentinfo"], #shopify-section-footer, .footer.full-width'
  };

  /**
   * observeAnyInView
   * Calls `onChange(true)` while ANY of `elements` intersects the viewport and
   * `onChange(false)` once none do. No-ops on an empty list or without
   * IntersectionObserver support (the bar then simply never hides for them).
   *
   * Tracks the intersecting set rather than assigning from each entry: the
   * callback only reports the entries that CHANGED, so with several observed
   * elements a naive per-entry assignment would let the last entry in the batch
   * clobber the state of the ones that did not fire.
   *
   * @param {Element[]} elements
   * @param {function(boolean)} onChange
   */
  function observeAnyInView(elements, onChange) {
    if (!elements.length || !('IntersectionObserver' in window)) return;
    var inView = [];
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var i = inView.indexOf(entry.target);
        if (entry.isIntersecting) {
          if (i === -1) inView.push(entry.target);
        } else if (i !== -1) {
          inView.splice(i, 1);
        }
      });
      onChange(inView.length > 0);
    }, { threshold: 0, rootMargin: '0px' });
    elements.forEach(function (el) { observer.observe(el); });
  }

  /**
   * initStickyCta
   * Attaches scroll listener and footer IntersectionObserver for one bar instance.
   * @param {HTMLElement} stickyCta - The sticky bar element.
   */
  function initStickyCta(stickyCta) {
    if (!stickyCta) return;

    // Remove the HTML `hidden` attribute so the element is in the layout flow
    // (display is still controlled by .is-visible / CSS transition).
    stickyCta.removeAttribute('hidden');

    var scrollPercentage = parseInt(stickyCta.getAttribute('data-scroll-percentage'), 10) || 0;
    var useScrollPercentage = scrollPercentage > 0;
    var triggerId = stickyCta.getAttribute('data-trigger-id') || '';

    // ── Desktop PDP bar: sit directly under the site header ──
    // The PDP bar is pinned to the top on desktop; rather than a manual offset
    // setting, measure the header's current bottom edge (announcement bar
    // included) and follow it as the header condenses on scroll. On mobile the
    // bar is bottom-anchored, so clear any inline top.
    var isPdp = stickyCta.classList.contains('sticky-add-to-cart--pdp');
    var headerEl = isPdp ? document.querySelector('[data-header]') : null;
    var desktopMq = window.matchMedia('(min-width: 750px)');

    function updateHeaderOffset() {
      if (!isPdp) return;
      if (headerEl && desktopMq.matches) {
        var bottom = Math.max(0, Math.round(headerEl.getBoundingClientRect().bottom));
        stickyCta.style.top = bottom + 'px';
      } else {
        // Mobile (bottom-anchored) or no header found — defer to the CSS.
        stickyCta.style.top = '';
      }
    }

    var triggerElement = null;
    if (!useScrollPercentage) {
      if (triggerId) {
        var triggerEl = document.getElementById(triggerId);
        if (triggerEl) {
          // Use the first h1 inside the trigger as the primary scroll anchor,
          // falling back to the trigger element itself.
          triggerElement = triggerEl.querySelector('h1') || triggerEl;
        }
      }
      // If neither scroll percentage nor a valid trigger element was found,
      // hide bar permanently rather than showing it immediately.
      if (!triggerElement) {
        // Guard: if trigger_id is set but element not found yet (e.g. lazy-loaded),
        // wait one tick and try again (theme editor re-init covers most cases).
        return;
      }
    }

    // Use the page footer landmark, NOT the first generic `<footer>` element.
    // Product cards can contain inner <footer> elements which would skew the check.
    var footer = document.querySelector(SELECTORS.footer);

    // Sections the bar stands down for. The selector is merchant-authored, so an
    // invalid one must fail open (bar behaves exactly as before) — an uncaught
    // throw here would take the scroll listener and footer observer below with
    // it and leave the bar stuck in its initial state.
    var hideOverSelector = (stickyCta.getAttribute('data-hide-over-selector') || '').trim();
    var hideOverTargets = [];
    if (hideOverSelector) {
      try {
        hideOverTargets = Array.prototype.slice.call(document.querySelectorAll(hideOverSelector));
      } catch {
        hideOverTargets = [];
      }
    }

    var hasScrolledPastTrigger = false;
    var footerVisible = false;
    var hideOverVisible = false;

    function updateVisibility() {
      var show = hasScrolledPastTrigger && !footerVisible && !hideOverVisible;
      stickyCta.classList.toggle('is-visible', show);
    }

    function checkScrollPosition() {
      // Keep the desktop PDP bar glued to the header's moving bottom edge.
      updateHeaderOffset();

      var scrolledPast = false;
      if (useScrollPercentage) {
        var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        var docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
        if (docHeight > 0) {
          scrolledPast = (scrollTop / docHeight) * 100 >= scrollPercentage;
        }
      } else if (triggerElement) {
        scrolledPast = triggerElement.getBoundingClientRect().bottom < 100;
      }
      if (hasScrolledPastTrigger !== scrolledPast) {
        hasScrolledPastTrigger = scrolledPast;
        updateVisibility();
      }
    }

    // requestAnimationFrame-debounced scroll listener
    var rafId = null;
    window.addEventListener('scroll', function () {
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(checkScrollPosition);
    }, { passive: true });

    // Header height also changes on resize and when crossing the mobile
    // breakpoint — re-measure then too (passive; cheap getBoundingClientRect).
    if (isPdp) {
      window.addEventListener('resize', updateHeaderOffset, { passive: true });
      if (desktopMq.addEventListener) {
        desktopMq.addEventListener('change', updateHeaderOffset);
      } else if (desktopMq.addListener) {
        desktopMq.addListener(updateHeaderOffset); // Safari < 14
      }
    }

    // Run once immediately to catch pages where the section is already past the trigger
    checkScrollPosition();

    // Footer visibility. Behaviour is unchanged from the previous inline
    // observer — for a single element `inView.length > 0` is exactly
    // `entry.isIntersecting`.
    observeAnyInView(footer ? [footer] : [], function (visible) {
      footerVisible = visible;
      updateVisibility();
    });

    // "Hide over" sections. IntersectionObserver rather than the rAF scroll
    // handler above on purpose: these targets also move when images finish
    // loading or a section re-renders in the editor, and IO reports that
    // without waiting for the next scroll event.
    observeAnyInView(hideOverTargets, function (visible) {
      hideOverVisible = visible;
      updateVisibility();
    });
  }

  /**
   * formatMoney — mirrors the formatter in product.js so the sticky price
   * matches the main price snippet ($X or $X.XX).
   */
  function formatMoney(cents) {
    var n = Number(cents) / 100;
    if (Number.isNaN(n)) return '';
    var rounded = Math.round(n * 100) / 100;
    var hasCents = rounded % 1 !== 0;
    return '$' + (hasCents ? rounded.toFixed(2) : rounded.toFixed(0));
  }

  /**
   * Keep the sticky PDP price in sync with the selected variant. product.js
   * dispatches `product:variant-change` with { sectionId, variant }; update
   * the matching bar's price element. Registered once at module scope.
   */
  document.addEventListener('product:variant-change', function (e) {
    if (!e.detail || !e.detail.variant) return;
    var priceEls = document.querySelectorAll(
      '[data-sticky-atc-price][data-section-id="' + e.detail.sectionId + '"]'
    );
    priceEls.forEach(function (el) {
      el.textContent = formatMoney(e.detail.variant.price);
    });
  });

  /**
   * init
   * Finds all sticky-atc bars within a given root element and initialises each.
   * @param {HTMLElement|Document} root - Container to search within.
   */
  /**
   * Mobile "Customize" button: reveal the variant dropdowns + ATC group by
   * adding `is-customizing` to the bar (CSS handles the show/hide).
   */
  function initCustomize(bar) {
    var btn = bar.querySelector('[data-sticky-customize]');
    if (!btn) return;
    btn.addEventListener('click', function () {
      bar.classList.add('is-customizing');
    });
  }

  /**
   * Current bottom edge of the sticky site header, so a jumplink target lands
   * just below it instead of behind it. Only counts when the header overlays
   * content (sticky/fixed); returns 0 for a static header that scrolls away.
   *
   * Measures .section-header, not the inner [data-header]. Stickiness is
   * applied to the section wrapper (see the comment on
   * `.section-header:has(.header--sticky)` in sections/header.liquid) — the
   * inner <header> is never sticky, so reading its computed position always
   * failed the guard below and this returned 0, putting jumplink targets
   * behind the header. assets/product.js:525 already measures .section-header
   * first for the same reason.
   */
  function getHeaderOffset() {
    var header = document.querySelector('.section-header') || document.querySelector('[data-header]');
    if (!header) return 0;
    var pos = window.getComputedStyle(header).position;
    if (pos !== 'sticky' && pos !== 'fixed') return 0;
    return Math.max(0, header.getBoundingClientRect().bottom);
  }

  /**
   * Smooth-scroll for the bar's jumplink button. The landing/band layouts link
   * out via a plain <a href="#section">; the theme has no global
   * `scroll-behavior: smooth`, so without this the jump is instant. Intercepts
   * only same-page hash links whose target exists (external links and the PDP
   * add-to-cart form are untouched) and offsets for the sticky header.
   */
  function initSmoothScroll(bar) {
    if (bar.dataset.smoothScrollInit) return;
    bar.dataset.smoothScrollInit = 'true';

    bar.addEventListener('click', function (e) {
      var link = e.target.closest('a[href]');
      if (!link || !bar.contains(link)) return;

      var url;
      try {
        url = new URL(link.getAttribute('href'), window.location.href);
      } catch {
        return;
      }
      // Different page → let the browser navigate normally.
      if (url.pathname !== window.location.pathname || url.search !== window.location.search) return;

      var id = decodeURIComponent(url.hash.slice(1));
      if (!id) return;
      var target = document.getElementById(id);
      if (!target) return;

      e.preventDefault();
      var top = target.getBoundingClientRect().top + window.pageYOffset - getHeaderOffset();
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });

      // Reflect the hash in the URL without triggering a second (instant) jump.
      if (window.history && window.history.pushState) {
        window.history.pushState(null, '', url.hash);
      }
    });
  }

  /**
   * Purchase-option (Skio selling plan) dropdown. The plan <select> lives in the
   * variant picker row; on change it mirrors the value into the form's
   * `selling_plan` hidden input and updates the sticky price to the selected
   * option's `data-plan-price`. On variant change it rebuilds the options from
   * the embedded per-variant plan map (plans/prices differ by variant) and
   * re-defaults to the subscription. No-op when the bar has no plan select.
   */
  function initPlanPicker(bar) {
    var select = bar.querySelector('[data-sticky-selling-plan]');
    if (!select) return;
    if (select.dataset.planInit) return; // avoid double-binding on re-init
    select.dataset.planInit = 'true';

    var sectionId = select.getAttribute('data-section-id');
    var priceEl = bar.querySelector('[data-sticky-atc-price]');

    var planMap = {};
    var mapScript = bar.querySelector('script[data-sticky-plan-map]');
    if (mapScript) {
      try { planMap = JSON.parse(mapScript.textContent); } catch (e) { planMap = {}; }
    }

    function sellingPlanInput() {
      var form = bar.querySelector('[data-product-form]');
      return form ? form.querySelector('[data-product-selling-plan-input]') : null;
    }

    // Push the current <select> value into the form input + sticky price.
    function applySelection() {
      var input = sellingPlanInput();
      if (input) input.value = select.value; // '' = one-time
      var opt = select.options[select.selectedIndex];
      var price = opt && opt.getAttribute('data-plan-price');
      if (priceEl && price != null && price !== '') {
        priceEl.textContent = formatMoney(parseInt(price, 10));
      }
    }

    select.addEventListener('change', applySelection);

    // Rebuild plan options for the newly selected variant, then re-default to
    // the subscription. Runs AFTER the module-level base-price handler, so the
    // plan price wins. source 'skio' is ignored (no plan picker on those bars).
    document.addEventListener('product:variant-change', function (e) {
      if (!e.detail || e.detail.sectionId !== sectionId || !e.detail.variant) return;
      var entry = planMap[e.detail.variant.id];
      var onetime = entry ? entry.onetime : e.detail.variant.price;

      while (select.options.length) select.remove(0);
      var ot = new Option('One-time purchase', '');
      ot.setAttribute('data-plan-price', onetime);
      select.add(ot);

      if (entry && entry.plans) {
        entry.plans.forEach(function (p) {
          var o = new Option('Subscribe — ' + p.name, String(p.id));
          o.setAttribute('data-plan-price', p.price);
          select.add(o);
        });
        select.value = entry.plans.length ? String(entry.plans[0].id) : '';
      } else {
        select.value = '';
      }
      applySelection();
    });

    // main → sticky plan sync: Skio dispatches `skio-price-update` with the
    // selected plan (or null = one-time) when the customer changes the
    // subscribe/one-time/frequency choice in the main product section. Mirror it
    // into this select when the matching option exists (the bar's plan options
    // track the current variant, kept aligned by the variant sync above).
    document.addEventListener('skio-price-update', function (e) {
      if (!e.detail) return;
      var sp = e.detail.selling_plan;
      var targetVal = sp && sp.id != null ? String(sp.id) : '';
      var exists = false;
      for (var i = 0; i < select.options.length; i++) {
        if (select.options[i].value === targetVal) { exists = true; break; }
      }
      if (exists && select.value !== targetVal) {
        select.value = targetVal;
        applySelection();
      }
    });

    // Sync the form input + price to the server-preselected option on load.
    applySelection();
  }

  /**
   * Cross-section variant sync (PDP bar ⇄ main product section). Keeps the
   * variant mirrored both ways: a variant change in either section sets the
   * other section's picker controls and lets THAT section's product.js resolve
   * the variant natively (price, variant input, URL, Skio). A `lastVariantId`
   * guard breaks the echo loop. Works for Skio and non-Skio products alike, and
   * no-ops when the bar isn't bound to a product.
   */
  function initCrossSectionSync(bar) {
    var stickySectionId = bar.getAttribute('data-product-section');
    if (!stickySectionId) return;
    if (bar.dataset.xsyncInit) return;
    bar.dataset.xsyncInit = 'true';

    var stickyPicker = bar.querySelector('[data-product-variant-picker]');
    var lastVariantId = null;

    // The "other" product section's picker (the main PDP buy box).
    function mainPicker() {
      var pickers = document.querySelectorAll('[data-product-variant-picker][data-section-id]');
      for (var i = 0; i < pickers.length; i++) {
        if (pickers[i].getAttribute('data-section-id') !== stickySectionId) return pickers[i];
      }
      return null;
    }

    // Set a picker's controls to `variant`, then nudge that section's product.js
    // to resolve it (dispatch the native change/click it already listens for).
    function syncPicker(pickerEl, variant) {
      if (!pickerEl || !variant || !variant.options) return;
      var changed = false;
      pickerEl.querySelectorAll('.product__variant-option[data-option-position]').forEach(function (optEl) {
        var pos = parseInt(optEl.getAttribute('data-option-position'), 10);
        var val = variant.options[pos - 1];
        if (val == null) return;

        var sel = optEl.querySelector('select[data-product-variant-select]');
        if (sel) {
          if (sel.value !== val) { sel.value = val; changed = true; }
          return;
        }
        var checked = optEl.querySelector('.product__variant-pill[aria-checked="true"]');
        if (checked && checked.getAttribute('data-option-value') === val) return;
        optEl.querySelectorAll('.product__variant-pill').forEach(function (p) {
          var on = p.getAttribute('data-option-value') === val;
          p.setAttribute('aria-checked', on ? 'true' : 'false');
          p.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        changed = true;
      });
      if (!changed) return;

      var anySelect = pickerEl.querySelector('select[data-product-variant-select]');
      if (anySelect) {
        anySelect.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        var firstChecked = pickerEl.querySelector('.product__variant-pill[aria-checked="true"]');
        if (firstChecked) firstChecked.dispatchEvent(new Event('click', { bubbles: true }));
      }
    }

    document.addEventListener('product:variant-change', function (e) {
      if (!e.detail || !e.detail.variant) return;
      var vid = String(e.detail.variant.id);
      if (vid === lastVariantId) return; // mirrored echo — ignore
      lastVariantId = vid;
      if (e.detail.sectionId === stickySectionId) {
        syncPicker(mainPicker(), e.detail.variant); // sticky → main
      } else {
        syncPicker(stickyPicker, e.detail.variant); // main → sticky
      }
    });
  }

  function init(root) {
    var bars = (root || document).querySelectorAll(SELECTORS.stickyCta);
    bars.forEach(function (bar) {
      initStickyCta(bar);
      initCustomize(bar);
      initSmoothScroll(bar);
      initPlanPicker(bar);
      initCrossSectionSync(bar);
    });
  }

  // ── Initial load ──
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // ── Theme editor: re-init when a section is loaded or re-selected ──
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:select', function (event) {
    init(event.target);
  });

})();
